<template>
  <div class="max-w-7xl mx-auto py-8">
    <!-- Page Header -->
    <div class="mb-8">
      <div class="bg-gradient-to-r from-orange-500 to-red-600 rounded-2xl p-8 text-white shadow-xl">
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-3xl font-bold mb-2">Invite Management</h1>
            <p class="text-orange-100 text-lg">Generate and manage customer invite tokens</p>
          </div>
          <button 
            @click="showGenerateModal = true"
            class="px-6 py-3 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl hover:bg-opacity-30 transition-all duration-200 text-white"
          >
            Generate New Token
          </button>
        </div>
      </div>
    </div>

    <!-- Invites Table -->
    <div class="bg-white shadow-lg rounded-xl overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-semibold text-gray-900">All Invite Tokens</h3>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="invite in invites" :key="invite.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-medium text-gray-900">{{ invite.company_name }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm font-mono text-gray-900">{{ invite.token }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  <div v-if="invite.email">{{ invite.email }}</div>
                  <div v-if="invite.phone">{{ invite.phone }}</div>
                  <div v-if="!invite.email && !invite.phone" class="text-gray-500">No contact info</div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span :class="getStatusClass(invite.status)" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full">
                  {{ getStatusText(invite.status) }}
                </span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(invite.expiry) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                {{ formatDate(invite.created_at) }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <button 
                  @click="copyToken(invite.token)"
                  class="text-orange-600 hover:text-orange-900 mr-3"
                >
                  Copy
                </button>
                <button 
                  v-if="invite.status === 'active'"
                  @click="revokeToken(invite.id)"
                  class="text-red-600 hover:text-red-900"
                >
                  Revoke
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Empty State -->
      <div v-if="invites.length === 0" class="text-center py-12">
        <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h3 class="text-lg font-medium text-gray-900 mb-2">No invite tokens yet</h3>
        <p class="text-gray-500 mb-4">Generate your first invite token to start onboarding customers</p>
        <button 
          @click="showGenerateModal = true"
          class="px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200"
        >
          Generate First Token
        </button>
      </div>
    </div>

    <!-- Generate Token Modal -->
    <div v-if="showGenerateModal" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/50 backdrop-blur-sm" @click="closeGenerateModal"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl border border-orange-200/20 max-w-md w-full p-8">
        <div class="text-center mb-6">
          <h3 class="text-2xl font-bold text-gray-900">Generate Invite Token</h3>
          <p class="text-gray-600 mt-2">Create a secure token for customer registration</p>
        </div>
        
        <form @submit.prevent="generateInviteToken" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
            <input 
              v-model="inviteForm.company_name" 
              type="text" 
              required
              placeholder="Enter company name"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Expiry (hours)</label>
              <input 
                v-model="inviteForm.expiry" 
                type="number" 
                min="1"
                max="168"
                placeholder="24"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Max Attempts</label>
              <input 
                v-model="inviteForm.max_attempts" 
                type="number" 
                min="1"
                max="10"
                placeholder="5"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
              <input 
                v-model="inviteForm.session_timeout_minutes" 
                type="number" 
                min="5"
                max="1440"
                placeholder="30"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input 
              v-model="inviteForm.email" 
              type="email" 
              placeholder="customer@company.com"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            <input 
              v-model="inviteForm.phone" 
              type="tel" 
              placeholder="+91 9876543210"
              class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          
          <div class="flex space-x-4 pt-4">
            <button 
              type="button"
              @click="closeGenerateModal"
              class="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              :disabled="generatingToken"
              class="flex-1 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-lg hover:from-orange-700 hover:to-red-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="generatingToken">Generating...</span>
              <span v-else>Generate Token</span>
            </button>
          </div>
        </form>
        
        <!-- Generated Token Display -->
        <div v-if="generatedToken" class="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h4 class="text-lg font-semibold text-green-800 mb-2">Token Generated Successfully!</h4>
          <div class="bg-white p-3 rounded border border-green-300">
            <p class="text-sm text-gray-600 mb-1">Token:</p>
            <p class="font-mono text-lg font-bold text-green-700 break-all">{{ generatedToken }}</p>
          </div>
          <p class="text-sm text-green-700 mt-2">
            Copy this token and share it with the customer (it is not sent automatically). It expires in {{ generatedExpiryHours }} hours.
          </p>
        </div>
        
        <!-- Error Display -->
        <div v-if="tokenError" class="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p class="text-red-700">{{ tokenError }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { adminAPI } from '@/services/api'
import { useNotificationStore } from '@/stores/notification'

export default {
  name: 'AdminInvites',
  setup() {
    const notificationStore = useNotificationStore()
    
    // State
    const invites = ref([])
    const loading = ref(false)
    const showGenerateModal = ref(false)
    const generatingToken = ref(false)
    const generatedToken = ref('')
    const generatedExpiryHours = ref(24)
    const tokenError = ref('')
    
    // Form data
    const inviteForm = ref({
      company_name: '',
      email: '',
      phone: '',
      expiry: 24,
      max_attempts: 5,
      session_timeout_minutes: 30
    })
    
    // Methods
    const loadInvites = async () => {
      try {
        loading.value = true
        const response = await adminAPI.getInvites()
        invites.value = response.data.invites
      } catch (error) {
        console.error('Failed to load invites:', error)
        notificationStore.showError('Error', 'Failed to load invite tokens')
      } finally {
        loading.value = false
      }
    }
    
    const generateInviteToken = async () => {
      try {
        generatingToken.value = true
        tokenError.value = ''
        generatedToken.value = ''
        
        const response = await adminAPI.generateInviteToken(inviteForm.value)
        
        generatedToken.value = response.data.invite.token
        generatedExpiryHours.value = inviteForm.value.expiry || 24
        
        // Reset form
        inviteForm.value = {
          company_name: '',
          email: '',
          phone: '',
          expiry: 24,
          max_attempts: 5,
          session_timeout_minutes: 30
        }
        
        // Reload invites
        await loadInvites()
        
        notificationStore.showSuccess('Success', 'Invite token generated successfully')
        
      } catch (error) {
        console.error('Failed to generate invite token:', error)
        tokenError.value = error.response?.data?.message || 'Failed to generate invite token'
        notificationStore.showError('Error', tokenError.value)
      } finally {
        generatingToken.value = false
      }
    }
    
    const closeGenerateModal = () => {
      showGenerateModal.value = false
      generatedToken.value = ''
      tokenError.value = ''
      inviteForm.value = {
        company_name: '',
        email: '',
        phone: '',
        expiry: 24,
        max_attempts: 5,
        session_timeout_minutes: 30
      }
    }
    
    const copyToken = async (token) => {
      try {
        await navigator.clipboard.writeText(token)
        notificationStore.showSuccess('Success', 'Token copied to clipboard')
      } catch (error) {
        console.error('Failed to copy token:', error)
        notificationStore.showError('Error', 'Failed to copy token')
      }
    }
    
    const revokeToken = async (inviteId) => {
      try {
        await adminAPI.revokeInviteToken(inviteId)
        await loadInvites()
        notificationStore.showSuccess('Success', 'Token revoked successfully')
      } catch (error) {
        console.error('Failed to revoke token:', error)
        notificationStore.showError('Error', 'Failed to revoke token')
      }
    }
    
    const getStatusClass = (status) => {
      const classMap = {
        active: 'bg-green-100 text-green-800',
        used: 'bg-blue-100 text-blue-800',
        expired: 'bg-yellow-100 text-yellow-800',
        revoked: 'bg-red-100 text-red-800'
      }
      return classMap[status] || 'bg-gray-100 text-gray-800'
    }
    
    const getStatusText = (status) => {
      const textMap = {
        active: 'Active',
        used: 'Used',
        expired: 'Expired',
        revoked: 'Revoked'
      }
      return textMap[status] || status
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
    
    onMounted(() => {
      loadInvites()
    })
    
    return {
      invites,
      loading,
      showGenerateModal,
      generatingToken,
      generatedToken,
      generatedExpiryHours,
      tokenError,
      inviteForm,
      generateInviteToken,
      closeGenerateModal,
      copyToken,
      revokeToken,
      getStatusClass,
      getStatusText,
      formatDate
    }
  }
}
</script> 